import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Draw a jsPDF-autotable that is guaranteed to fit within a fixed vertical
 * budget, so a result never spills onto a second page.
 *
 * The natural (base) size matches the existing design. When the table would be
 * too tall (many subjects and/or wrapped subject names), the body/header font
 * and cell padding are shrunk — only as much as needed — until the whole table
 * fits within `availableHeight`. Wrapping is accounted for accurately by
 * measuring the real rendered height in a throwaway document before drawing.
 *
 * @param {jsPDF} doc            Live document to draw into.
 * @param {object} params
 * @param {(font: {body:number, head:number, pad:number}) => {head:Array, body:Array, columnStyles?:object}} params.buildTable
 *        Builds the head/body for a given font config. Cells must use
 *        `font.body` / `font.head` for their fontSize so shrinking takes effect.
 * @param {number} params.startY          Y (mm) where the table body begins.
 * @param {number} params.availableHeight Vertical budget (mm) the table must fit in.
 * @param {object} params.tableOptions    Shared autoTable options (theme, colors, margins, tableWidth, base styles/headStyles).
 * @param {{body:number, head:number, pad:number}} [params.base]  Natural (max) sizes. Defaults to the current design.
 * @param {{body:number, head:number, pad:number}} [params.floor] Readable minimum sizes.
 * @returns {number} finalY of the drawn table.
 */
export const drawFittedTable = (
  doc,
  {
    buildTable,
    startY,
    availableHeight,
    tableOptions = {},
    base = { body: 7.5, head: 8, pad: 2 },
    floor = { body: 4.5, head: 5, pad: 0.6 },
  }
) => {
  const measureAt = (font) => {
    const { head, body, columnStyles } = buildTable(font);
    const meas = new jsPDF('p', 'mm', 'a4');
    meas.autoTable({
      ...tableOptions,
      head,
      body,
      columnStyles,
      startY: 0,
      styles: { ...(tableOptions.styles || {}), fontSize: font.body, cellPadding: font.pad },
      headStyles: { ...(tableOptions.headStyles || {}), fontSize: font.head },
    });
    return meas.lastAutoTable.finalY;
  };

  let font = { ...base };
  // Iteratively shrink until the measured height fits or we hit the readable floor.
  for (let i = 0; i < 6; i++) {
    const measuredHeight = measureAt(font);
    if (measuredHeight <= availableHeight) break;
    if (font.body <= floor.body && font.pad <= floor.pad) break;
    // Damped scale (sqrt) avoids overshooting since wrapping eases as font drops.
    const scale = Math.max(0.6, Math.sqrt(availableHeight / measuredHeight));
    font = {
      body: Math.max(floor.body, font.body * scale),
      head: Math.max(floor.head, font.head * scale),
      pad: Math.max(floor.pad, font.pad * scale),
    };
  }

  const { head, body, columnStyles } = buildTable(font);
  doc.autoTable({
    ...tableOptions,
    head,
    body,
    columnStyles,
    startY,
    styles: { ...(tableOptions.styles || {}), fontSize: font.body, cellPadding: font.pad },
    headStyles: { ...(tableOptions.headStyles || {}), fontSize: font.head },
  });
  return doc.lastAutoTable.finalY;
};
